import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Routes from '@/router/routes';
import { ArrowLeft, UserPlus, MoreHorizontal, Mail, Trash2, Plus } from 'lucide-react';
import {
  useOrganization,
  useOrganizationMembers,
  useOrganizationWorkspaces,
  useUpdateSubscription,
  useInviteUser,
  useResendInvite,
  useRemoveMember,
  useUpdateOrganizationMemberRole,
} from '@/hooks/api/useOrganizations';
import { useCreateWorkspace } from '@/hooks/api/useWorkspaces';
import { OrganizationMember } from '@/api/organizations';
import { CreateWorkspaceDialog } from '@/components/dialogs/CreateWorkspaceDialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '@/components/ui/dropdown-menu';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { InviteMemberDialog } from '@/components/dialogs/InviteMemberDialog';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { formatDate, formatDateTime } from '@/lib/date';

export default function SuperAdminSingleOrganization() {
  const { org_id } = useParams<{ org_id: string }>();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('overview');
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [createWorkspaceDialogOpen, setCreateWorkspaceDialogOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    onConfirm: () => void;
    variant?: 'default' | 'destructive';
  }>({
    open: false,
    title: '',
    description: '',
    onConfirm: () => {},
  });

  // Queries
  const { data: organization, isLoading: orgLoading, isError: orgError, refetch: refetchOrg } = useOrganization(org_id || '');
  const { data: membersData, isLoading: membersLoading } = useOrganizationMembers(org_id || '', {
    page: 1,
    limit: 100,
  });
  const { data: workspacesData, isLoading: workspacesLoading } = useOrganizationWorkspaces(
    org_id || '',
  );

  // Mutations
  const updateSubscription = useUpdateSubscription();
  const createWorkspace = useCreateWorkspace();
  const inviteUser = useInviteUser();
  const resendInvite = useResendInvite();
  const removeMember = useRemoveMember();
  const updateMemberRole = useUpdateOrganizationMemberRole();

  const handleBack = () => {
    navigate(Routes.super_admin_dashboard_organizations.path);
  };

  const handleCreateWorkspace = (name: string, ownerEmail: string, organizationId: string) => {
    createWorkspace.mutate(
      { organizationId, payload: { name, ownerEmail } },
      {
        onSuccess: (ws) => {
          setCreateWorkspaceDialogOpen(false);
          navigate(`${Routes.super_admin_dashboard_workspaces.path}/${ws._id}`);
        },
      }
    );
  };

  const handleToggleSubscription = (subscribed: boolean) => {
    if (!organization) return;

    setConfirmDialog({
      open: true,
      title: `${subscribed ? 'Activate' : 'Deactivate'} Subscription`,
      description: `Are you sure you want to ${subscribed ? 'activate' : 'deactivate'} the subscription for ${organization.name}?`,
      onConfirm: () => {
        updateSubscription.mutate(
          { id: organization._id, payload: { subscribed } },
          { onSuccess: () => setConfirmDialog((prev) => ({ ...prev, open: false })) }
        );
      },
    });
  };

  const handleInviteMember = (
    email: string,
    role: string,
    workspaceIds?: string[],
    workspaceRole?: 'CAN_EDIT' | 'CAN_VIEW'
  ) => {
    if (!org_id) return;
    inviteUser.mutate(
      { id: org_id, payload: { email, role, workspaceIds, workspaceRole } },
      {
        onSuccess: () => {
          setInviteDialogOpen(false);
        },
      },
    );
  };

  const handleResendInvite = (member: OrganizationMember) => {
    if (!org_id) return;
    resendInvite.mutate({ id: org_id, email: member.email });
  };

  const handleRemoveMember = (member: OrganizationMember) => {
    if (!org_id) return;
    setConfirmDialog({
      open: true,
      title: 'Remove Member',
      description: `Are you sure you want to remove ${member.name || member.email} from this organization?`,
      onConfirm: () => {
        removeMember.mutate(
          { id: org_id, userId: member._id },
          { onSuccess: () => setConfirmDialog((prev) => ({ ...prev, open: false })) }
        );
      },
      variant: 'destructive',
    });
  };

  const handleChangeRole = (member: OrganizationMember, newRole: string) => {
    if (!org_id) return;
    updateMemberRole.mutate({ id: org_id, userId: member._id, role: newRole });
  };

  if (orgLoading) {
    return (
      <div className="flex flex-col gap-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (orgError) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <p className="text-red-600 mb-4">Failed to load data. Please try again.</p>
        <Button variant="outline" onClick={() => refetchOrg()}>
          Retry
        </Button>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="flex flex-col gap-6">
        <Button variant="ghost" onClick={handleBack} className="w-fit">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Organizations
        </Button>
        <div className="bg-card rounded-lg shadow-sm border border-border p-8 text-center">
          <p className="text-red-600">Organization not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <Breadcrumbs items={[
        { label: 'Organizations', path: Routes.super_admin_dashboard_organizations.path },
        { label: organization.name },
      ]} />

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={handleBack} size="icon">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold text-foreground">{organization.name}</h1>
              <Badge variant={organization.subscribed ? 'paid' : 'free'}>
                {organization.subscribed ? 'Paid' : 'Free'}
              </Badge>
              {organization.deletedAt && <Badge variant="deleted">Deleted</Badge>}
            </div>
            <p className="text-muted-foreground mt-1">
              Owner: {organization.owner?.name || organization.owner?.email || 'N/A'}
            </p>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="members">Members</TabsTrigger>
          <TabsTrigger value="workspaces">Workspaces</TabsTrigger>
        </TabsList>

        <TabsContent value="members" className="mt-6">
          <div className="bg-card rounded-lg shadow-sm border border-border">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h2 className="text-lg font-semibold">Members</h2>
              <Button onClick={() => setInviteDialogOpen(true)} size="sm">
                <UserPlus className="h-4 w-4 mr-2" />
                Invite Member
              </Button>
            </div>

            {membersLoading ? (
              <div className="p-4 space-y-3">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : !membersData || !membersData.data || membersData.data.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-muted-foreground">No members found</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead className="w-[70px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {membersData?.data?.map((member) => (
                    <TableRow key={member._id}>
                      <TableCell className="font-medium">
                        {[member.name, member.last_name].filter(Boolean).join(' ') || 'N/A'}
                      </TableCell>
                      <TableCell>{member.email}</TableCell>
                      <TableCell>
                        <span className="capitalize">{member.role}</span>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            member.status === 'active'
                              ? 'active'
                              : member.status === 'pending'
                                ? 'pending'
                                : 'default'
                          }
                        >
                          {member.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDate(member.joinedAt || member.invitedAt)}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {member.status === 'pending' && (
                              <>
                                <DropdownMenuItem onClick={() => handleResendInvite(member)}>
                                  <Mail className="h-4 w-4 mr-2" />
                                  Resend Invite
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                              </>
                            )}
                            {member.role !== 'OWNER' && (
                              <>
                                <DropdownMenuSub>
                                  <DropdownMenuSubTrigger>
                                    Change Role
                                  </DropdownMenuSubTrigger>
                                  <DropdownMenuSubContent>
                                    <DropdownMenuItem
                                      onClick={() => handleChangeRole(member, 'ADMIN')}
                                      disabled={member.role === 'ADMIN'}
                                    >
                                      Admin
                                      {member.role === 'ADMIN' && ' (current)'}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => handleChangeRole(member, 'EDITOR')}
                                      disabled={member.role === 'EDITOR'}
                                    >
                                      Editor
                                      {member.role === 'EDITOR' && ' (current)'}
                                    </DropdownMenuItem>
                                  </DropdownMenuSubContent>
                                </DropdownMenuSub>
                                <DropdownMenuSeparator />
                              </>
                            )}
                            <DropdownMenuItem
                              onClick={() => handleRemoveMember(member)}
                              className="text-red-600"
                              disabled={member.role === 'OWNER'}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Remove Member
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="workspaces" className="mt-6">
          <div className="bg-card rounded-lg shadow-sm border border-border">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h2 className="text-lg font-semibold">Workspaces</h2>
              <Button onClick={() => setCreateWorkspaceDialogOpen(true)} size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Create Workspace
              </Button>
            </div>

            {workspacesLoading ? (
              <div className="p-4 space-y-3">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : !workspacesData || !workspacesData.data || workspacesData.data.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-muted-foreground">No workspaces found</p>
              </div>
            ) : (
              <Accordion type="single" collapsible className="px-4">
                {workspacesData?.data?.map((workspace) => (
                  <AccordionItem key={workspace._id} value={workspace._id}>
                    <AccordionTrigger>
                      <div className="flex items-center justify-between flex-1 pr-4">
                        <Link
                          to={`${Routes.super_admin_dashboard_workspaces.path}/${workspace._id}`}
                          className="font-medium text-blue-600 hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {workspace.name}
                        </Link>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span>{workspace.projectCount || 0} projects</span>
                          <span>{workspace.memberCount || 0} members</span>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="pl-4 pr-2 pb-2 space-y-2">
                        <div className="text-sm text-muted-foreground">
                          <span className="font-medium">Created:</span> {formatDate(workspace.createdAt)}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          <span className="font-medium">Projects:</span> {workspace.projectCount || 0}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          <span className="font-medium">Members:</span> {workspace.memberCount || 0}
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </div>
        </TabsContent>

        <TabsContent value="overview" className="mt-6">
          <div className="grid gap-6 md:grid-cols-2">
            <div className="bg-card rounded-lg shadow-sm border border-border p-6">
              <h3 className="text-lg font-semibold mb-4">Organization Stats</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Members</span>
                  <span className="font-semibold">{organization.memberCount || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Workspaces</span>
                  <span className="font-semibold">{organization.workspaceCount || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Projects</span>
                  <span className="font-semibold">{organization.projectCount || 0}</span>
                </div>
              </div>
            </div>

            <div className="bg-card rounded-lg shadow-sm border border-border p-6">
              <h3 className="text-lg font-semibold mb-4">Settings</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Subscription</p>
                    <p className="text-sm text-muted-foreground">
                      {organization.subscribed ? 'Paid plan active' : 'Free plan'}
                    </p>
                  </div>
                  <Switch
                    checked={organization.subscribed}
                    onCheckedChange={handleToggleSubscription}
                    disabled={updateSubscription.isPending || !!organization.deletedAt}
                  />
                </div>
              </div>
            </div>

            <div className="bg-card rounded-lg shadow-sm border border-border p-6">
              <h3 className="text-lg font-semibold mb-4">Organization Details</h3>
              <div className="space-y-3">
                <div>
                  <span className="text-muted-foreground text-sm">Created</span>
                  <p className="font-medium">{formatDateTime(organization.createdAt)}</p>
                </div>
                <div>
                  <span className="text-muted-foreground text-sm">Last Updated</span>
                  <p className="font-medium">{formatDateTime(organization.updatedAt)}</p>
                </div>
                <div>
                  <span className="text-muted-foreground text-sm">Owner</span>
                  <p className="font-medium">{organization.owner?.name || 'N/A'}</p>
                  <p className="text-sm text-muted-foreground">{organization.owner?.email || 'N/A'}</p>
                </div>
              </div>
            </div>

            <div className="bg-card rounded-lg shadow-sm border border-border p-6">
              <h3 className="text-lg font-semibold mb-4">Organization ID</h3>
              <div className="space-y-3">
                <div>
                  <span className="text-muted-foreground text-sm">MongoDB ID</span>
                  <p className="font-mono text-sm bg-muted p-2 rounded mt-1 break-all">
                    {organization._id}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <InviteMemberDialog
        open={inviteDialogOpen}
        onOpenChange={setInviteDialogOpen}
        onInvite={handleInviteMember}
        loading={inviteUser.isPending}
        organizationId={org_id}
      />

      <CreateWorkspaceDialog
        open={createWorkspaceDialogOpen}
        onOpenChange={setCreateWorkspaceDialogOpen}
        onSubmit={handleCreateWorkspace}
        loading={createWorkspace.isPending}
        organizationId={org_id}
        organizationName={organization.name}
      />

      <ConfirmDialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, open })}
        title={confirmDialog.title}
        description={confirmDialog.description}
        onConfirm={confirmDialog.onConfirm}
        variant={confirmDialog.variant}
        loading={updateSubscription.isPending || removeMember.isPending}
      />
    </div>
  );
}
